import { Component, ElementRef, ViewChild } from '@angular/core';
import { CurrencyPipe, DatePipe, NgClass } from '@angular/common';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { forkJoin } from 'rxjs';
import { VenuePriceImportPreview, VenuePriceImportPreviewRow, VenueService } from '../shared/venue.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { VenueModel } from '../shared/venue.model';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { Calendar, LucideAngularModule, Save, X, CheckCircle, GripVertical } from 'lucide-angular';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { Countries } from '@app/shared/countries';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { AbstractEntityEditComponent } from '@app/shared/abstract/abstract-entity-edit.component';
import { NotificationService } from '@app/shared/notification/notification.service';
import { AuthService } from '@app/auth/auth.service';
import { SearchableSelectComponent } from '@app/partials/form/searchable-select/searchable-select.component';
import { VenueQuoteAssignmentService, VenueQuoteAssignmentResponse } from '@app/venues/shared/venue-quote-assignment.service';
import { ProductService } from '@app/products/shared/product.service';
import { ProductModel } from '@app/products/shared/product.model';
import { ProductVariantModel } from '@app/products/shared/product-variant.model';
import { ProductVariantOptionModel } from '@app/products/shared/product-variant-option.model';
import { ModalComponent } from '@app/partials/modal/modal.component';

@Component({
  selector: 'app-venue-edit',
  imports: [
    ReactiveFormsModule,
    TextInputComponent,
    LucideAngularModule,
    ButtonComponent,
    SelectComponent,
    RouterLink,
    SearchableSelectComponent,
    ModalComponent,
    CurrencyPipe,
    DatePipe,
    NgClass,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
  ],
  templateUrl: './venue-edit.component.html',
  styleUrl: './venue-edit.component.css',
})
export class VenueEditComponent extends AbstractEntityEditComponent {
  @ViewChild('csvFileInput') csvFileInput!: ElementRef<HTMLInputElement>;

  protected processing = false;
  protected validationErrors: string[] = [];
  protected venue: VenueModel;
  protected readonly SaveIcon = Save;
  protected readonly CalendarIcon = Calendar;
  protected readonly XIcon = X;
  protected readonly CheckCircleIcon = CheckCircle;
  protected readonly GripVerticalIcon = GripVertical;
  protected readonly Countries = Countries;
  protected isAdmin = false;
  protected savingAssignments = false;
  private assignmentsLoaded = false;
  private optionsLoaded = false;
  protected showImportPreview = false;
  protected importPreview: VenuePriceImportPreview | null = null;
  protected importPreviewLoading = false;
  protected importConfirming = false;
  private pendingImportFile: File | null = null;
  protected showImportSheetPicker = false;
  protected importSheetChoices: string[] = [];
  protected selectedImportSheetName: string | null = null;
  protected showImportErrorModal = false;
  protected importErrorTitle = 'Price Import Error';
  protected importErrorNote: string | null = null;
  protected importErrorItems: { title: string; message: string; examples: string[] }[] = [];
  protected quoteProducts: Record<string, ProductModel[]> = {
    canapes: [],
    wedding_breakfast_menu: [],
    wedding_drinks_package: [],
    evening_food: [],
  };
  protected quoteProductControls: Record<string, FormControl<number | null>> = {
    canapes: new FormControl<number | null>(null),
    wedding_breakfast_menu: new FormControl<number | null>(null),
    wedding_drinks_package: new FormControl<number | null>(null),
    evening_food: new FormControl<number | null>(null),
  };
  protected quoteProductChoices: Record<string, { value: number; label: string; meta?: any }[]> = {
    canapes: [],
    wedding_breakfast_menu: [],
    wedding_drinks_package: [],
    evening_food: [],
  };
  protected quoteProductSearchFunctions: Record<
    string,
    (term: string, page?: number) => ReturnType<ProductService['searchProducts']>
  > = {};
  protected readonly quoteProductSections = [
    { slug: 'canapes', label: 'Canapes', emptyLabel: 'No canapes selected yet.' },
    { slug: 'wedding_breakfast_menu', label: 'Breakfast', emptyLabel: 'No breakfast products selected yet.' },
    { slug: 'wedding_drinks_package', label: 'Drinks', emptyLabel: 'No drinks selected yet.' },
    { slug: 'evening_food', label: 'Evening food', emptyLabel: 'No evening food products selected yet.' },
  ];

  imageUrl: string | ArrayBuffer | null = null;
  imageFile: File | null = null;

  logoUrl: string | ArrayBuffer | null = null;
  logoFile: File | null = null;

  mapUrl: string | ArrayBuffer | null = null;
  mapFile: File | null = null;

  quoteAssignmentControls: Record<string, FormControl<number[] | null>> = {
    canapes: new FormControl<number[] | null>([]),
    wedding_breakfast_menu: new FormControl<number[] | null>([]),
    wedding_drinks_package: new FormControl<number[] | null>([]),
    evening_food: new FormControl<number[] | null>([]),
  };
  quoteVariantOptionControls: Record<string, FormControl<number[] | null>> = {
    canapes: new FormControl<number[] | null>([]),
    wedding_breakfast_menu: new FormControl<number[] | null>([]),
    wedding_drinks_package: new FormControl<number[] | null>([]),
    evening_food: new FormControl<number[] | null>([]),
  };

  private readonly allowedCategoriesMap: Record<string, string[]> = {
    canapes: ['Canapes'],
    wedding_breakfast_menu: ['Wedding Breakfast Menus'],
    wedding_drinks_package: ['Drinks Packages', 'Wine List'],
    evening_food: ['Evening Food', 'Evening Food – Complimentary'],
  };

  form = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]),
    address: new FormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(100)]),
    city: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]),
    region: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]),
    postal_code: new FormControl('', [Validators.required]),
    country: new FormControl('', Validators.required),
    phone: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    website: new FormControl('', [
      Validators.required,
      Validators.pattern('^(https?:\\/\\/)?([\\da-z.-]+)\\.([a-z.]{2,6})([\\/\\w .-]*)*\\/?$'),
    ]),
    info: new FormControl('', Validators.required),
  });

  constructor(
    private venueService: VenueService,
    private route: ActivatedRoute,
    private router: Router,
    private notificationService: NotificationService,
    private authService: AuthService,
    private venueQuoteAssignmentService: VenueQuoteAssignmentService,
    private productService: ProductService,
  ) {
    super();
    this.quoteProductSections.forEach((section) => {
      const slug = section.slug;
      this.quoteProductSearchFunctions[slug] = (term: string, page?: number) =>
        this.productService.searchProducts(term, page || 1, 50, { categories: this.allowedCategoriesMap[slug] });

      this.quoteProductControls[slug].valueChanges.subscribe((productId) => {
        if (productId) {
          this.addQuoteProduct(slug, productId);
          this.quoteProductControls[slug].setValue(null, { emitEvent: false });
        }
      });
    });

    this.authService.getUser().subscribe({
      next: (user) => {
        this.isAdmin = user?.roles?.includes('admin') ?? false;
        this.maybeLoadAssignments();
        this.maybeLoadOptions();
      },
      error: () => {
        this.isAdmin = false;
      },
    });

    this.form.valueChanges.subscribe((value) => {
      if (this.form.dirty && !this.form.valid) {
        this.validationErrors = this.getFormValidationErrors(this.form);
      }
    });

    if (this.route.snapshot.data['venue']) {
      this.venue = this.route.snapshot.data['venue'];
      this.form.patchValue(this.venue);
      this.maybeLoadAssignments();
      this.maybeLoadOptions();
    } else {
      this.venue = {} as VenueModel;
      this.venue.images = [];
      this.venue.logos = [];
      this.venue.maps = [];
      this.venue.country = 'United Kingdom';
      this.form.patchValue(this.venue);
    }
  }

  save() {
    this.performSave(false);
  }

  saveAndContinue() {
    this.performSave(true);
  }

  private performSave(continueEditing: boolean) {
    if (this.form.invalid) {
      return;
    }
    this.processing = true;

    const formData = new FormData();
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control) {
        formData.append(key, control.value);
      }
    });
    if (this.imageFile) {
      formData.append('image', this.imageFile);
    }
    if (this.logoFile) {
      formData.append('logo', this.logoFile);
    }
    if (this.mapFile) {
      formData.append('map', this.mapFile);
    }

    this.venueService.saveFormData(formData, this.venue?.id).subscribe({
      next: (res) => {
        this.processing = false;
        this.venue = res.data;
        this.form.patchValue(this.venue);
        this.form.markAsPristine();
        this.notificationService.success('Venue saved successfully');

        if (continueEditing) {
          // For saveAndContinue, stay on edit page
          if (!this.venue.id && res.data) {
            this.router.navigate(['/venues/' + res.data.id + '/edit']);
          }
          // If editing existing venue, just stay on current page (no navigation)
        } else {
          // For regular save, go to index page
          this.router.navigate(['/venues']);
        }
      },
      error: (res) => {
        if (res.status === 422) {
          AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
          this.notificationService.error('Please correct the validation errors');
        } else {
          this.notificationService.error('An error occurred while saving the venue');
        }
        this.processing = false;
      },
    });
  }

  cancel() {
    return this.router.navigate(['/venues']);
  }

  onFileChange(event: Event, fileType: 'image' | 'logo' | 'map') {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        if (fileType === 'image') {
          this.imageFile = file;
          this.imageUrl = reader.result;
        } else if (fileType === 'logo') {
          this.logoFile = file;
          this.logoUrl = reader.result;
        } else if (fileType === 'map') {
          this.mapFile = file;
          this.mapUrl = reader.result;
        }
      };
      this.form.markAsDirty();
      reader.readAsDataURL(file);
    }
  }

  exportPricesCSV() {
    if (!this.venue || !this.venue.id) {
      this.notificationService.warning('Please save the venue first');
      return;
    }
    this.venueService.exportPricesCSV(this.venue.id).subscribe({
      next: (res) => {
        const blob = new Blob([res], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.venue.name}-prices.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        this.notificationService.success('CSV exported successfully');
      },
      error: (error) => {
        this.notificationService.error('Error exporting CSV');
      },
    });
  }

  importPricesCSV() {
    if (this.csvFileInput) {
      this.csvFileInput.nativeElement.click();
    }
  }

  onCsvFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const fileName = file.name.toLowerCase();
      const isXlsx = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || fileName.endsWith('.xlsx');
      const isXls = file.type === 'application/vnd.ms-excel' || fileName.endsWith('.xls');

      if (!isXlsx && !isXls) {
        this.notificationService.warning('Please select an XLSX file');
        return;
      }

      this.importPreviewLoading = true;
      this.pendingImportFile = file;
      const formData = new FormData();
      formData.append('csv_file', file);

      if (this.venue.id) {
        this.venueService.previewPricesImport(this.venue.id, formData).subscribe({
          next: (response) => {
            this.importPreviewLoading = false;
            this.importPreview = response;
            this.pendingImportFile = file;
            this.showImportPreview = true;
            this.resetImportInput();
          },
          error: (error) => {
            this.importPreviewLoading = false;
            console.error('Error previewing prices:', error);
            const sheets = Array.isArray(error?.error?.sheets) ? error.error.sheets : [];
            if (sheets.length > 1) {
              this.importSheetChoices = sheets;
              // Force explicit selection to avoid importing the wrong sheet.
              this.selectedImportSheetName = null;
              this.showImportSheetPicker = true;
              this.resetImportInput();
              return;
            }
            this.showImportErrorsInModal(error, 'Error previewing prices');
            this.resetImportInput();
          },
        });
      } else {
        this.notificationService.warning('Please save the venue first before importing prices');
        this.importPreviewLoading = false;
        this.resetImportInput();
      }
    }
  }

  closeImportPreview(): void {
    if (this.importConfirming) {
      return;
    }

    this.showImportPreview = false;
    this.importPreview = null;
    this.pendingImportFile = null;
    this.showImportSheetPicker = false;
    this.importSheetChoices = [];
    this.selectedImportSheetName = null;
    this.closeImportErrorModal();
    this.resetImportInput();
  }

  closeImportSheetPicker(): void {
    if (this.importPreviewLoading || this.importConfirming) {
      return;
    }

    this.showImportSheetPicker = false;
    this.importSheetChoices = [];
    this.selectedImportSheetName = null;
    this.pendingImportFile = null;
    this.closeImportErrorModal();
    this.resetImportInput();
  }

  confirmImportSheetChoice(): void {
    if (!this.venue.id || !this.pendingImportFile || !this.selectedImportSheetName) {
      this.notificationService.warning('Please select a sheet to import');
      return;
    }

    this.importPreviewLoading = true;
    const formData = new FormData();
    formData.append('csv_file', this.pendingImportFile);
    formData.append('sheet_name', this.selectedImportSheetName);

    this.venueService.previewPricesImport(this.venue.id, formData).subscribe({
      next: (response) => {
        this.importPreviewLoading = false;
        this.importPreview = response;
        this.showImportSheetPicker = false;
        this.showImportPreview = true;
        this.closeImportErrorModal();
        this.resetImportInput();
      },
      error: (error) => {
        this.importPreviewLoading = false;
        console.error('Error previewing prices:', error);
        this.showImportErrorsInModal(error, 'Error previewing prices');
        // Keep the sheet picker open so the user can try a different sheet without re-uploading.
      },
    });
  }

  confirmImportPrices(): void {
    if (!this.venue.id || !this.pendingImportFile) {
      this.notificationService.warning('Please select a file to import');
      this.closeImportPreview();
      return;
    }

    this.importConfirming = true;
    const formData = new FormData();
    formData.append('csv_file', this.pendingImportFile);
    if (this.selectedImportSheetName) {
      formData.append('sheet_name', this.selectedImportSheetName);
    }

    this.venueService.importPricesCSV(this.venue.id, formData).subscribe({
      next: () => {
        this.importConfirming = false;
        this.notificationService.success('Prices imported successfully');
        this.showImportPreview = false;
        this.importPreview = null;
        this.pendingImportFile = null;
        this.importSheetChoices = [];
        this.selectedImportSheetName = null;
        this.resetImportInput();
      },
      error: (error) => {
        this.importConfirming = false;
        console.error('Error importing prices:', error);
        this.showImportErrorsInModal(error, 'Error importing prices');
      },
    });
  }

  protected getForms(): FormGroup<any>[] {
    return [this.form];
  }

  private maybeLoadAssignments(): void {
    if (!this.isAdmin || this.assignmentsLoaded || !this.venue?.id) {
      return;
    }

    this.assignmentsLoaded = true;
    this.venueQuoteAssignmentService.getAssignments(this.venue.id).subscribe({
      next: (response: VenueQuoteAssignmentResponse) => {
        response.data.categories.forEach((category) => {
          const productItems = category.products.filter((item) => item.item_type !== 'variant_option');
          const variantItems = category.products.filter((item) => item.item_type === 'variant_option');
          const productIds = productItems.map((item) => item.id);
          const variantIds = variantItems.map((item) => item.id);

          this.quoteAssignmentControls[category.slug]?.setValue(productIds);
          this.quoteVariantOptionControls[category.slug]?.setValue(variantIds);

          // Build the product-card order exactly as the saved sort_order dictates:
          // assigned products first (in order), then the parent products of any
          // assigned variant options (in order). Hydrating in this order keeps the
          // control panel showing the order staff saved.
          const orderedCardProductIds: number[] = [];
          productItems.forEach((item) => {
            if (typeof item.id === 'number') {
              orderedCardProductIds.push(item.id);
            }
          });
          variantItems.forEach((item) => {
            if (typeof item.product_id === 'number') {
              orderedCardProductIds.push(item.product_id);
            }
          });

          this.hydrateQuoteProductCards(category.slug, orderedCardProductIds);
        });
      },
      error: () => {
        this.assignmentsLoaded = false;
      },
    });
  }

  /**
   * Fetch the given products and populate the category's card list in the exact
   * order supplied. Using forkJoin (instead of firing independent requests) keeps
   * the displayed order deterministic so the saved drag order always round-trips.
   *
   * @param slug              Category slug to hydrate.
   * @param orderedProductIds Product ids in the order they should be displayed.
   */
  private hydrateQuoteProductCards(slug: string, orderedProductIds: number[]): void {
    const uniqueIds = orderedProductIds.filter((id, index) => orderedProductIds.indexOf(id) === index);
    if (uniqueIds.length === 0) {
      this.quoteProducts[slug] = [];
      return;
    }

    forkJoin(uniqueIds.map((id) => this.productService.getById(id))).subscribe({
      next: (responses) => {
        const productsById = new Map<number, ProductModel>();
        responses.forEach((response) => {
          const product = response.data;
          if (product?.id) {
            productsById.set(product.id, product);
          }
        });

        this.quoteProducts[slug] = uniqueIds
          .map((id) => productsById.get(id))
          .filter((product): product is ProductModel => !!product);
      },
    });
  }

  private resetImportInput(): void {
    if (this.csvFileInput) {
      this.csvFileInput.nativeElement.value = '';
    }
  }

  private buildImportErrorMessage(error: any, fallbackMessage: string): string {
    const details = Array.isArray(error?.error?.details)
      ? error.error.details
      : Object.values(error?.error?.errors || {}).flat();
    const baseMessage = error?.error?.error || fallbackMessage;

    return details.length > 0 ? `${baseMessage}: ${details.join(' | ')}` : baseMessage;
  }

  protected closeImportErrorModal(): void {
    this.showImportErrorModal = false;
    this.importErrorNote = null;
    this.importErrorItems = [];
  }

  private showImportErrorsInModal(error: any, fallbackTitle: string): void {
    const baseMessage = String(error?.error?.error || fallbackTitle);
    const details = Array.isArray(error?.error?.details)
      ? error.error.details
      : Object.values(error?.error?.errors || {}).flat();
    const meta = error?.error?.meta || null;

    this.importErrorTitle = baseMessage;
    const normalized = details
      .map((d: any) => String(d))
      .filter((d: string) => d.trim() !== '');

    this.importErrorNote = meta?.details_total && meta?.details_truncated
      ? `Showing ${meta.details_shown} of ${meta.details_total} issues.`
      : null;

    this.importErrorItems = this.parseImportErrorDetails(normalized.length > 0 ? normalized : [baseMessage]);
    this.showImportErrorModal = true;
  }

  private parseImportErrorDetails(lines: string[]): { title: string; message: string; examples: string[] }[] {
    const items: { title: string; message: string; examples: string[] }[] = [];
    const others: string[] = [];

    for (const rawLine of lines) {
      const line = rawLine.trim();
      if (!line) continue;

      const [head, tail] = line.split(/Examples:\s*/i);
      const message = (head || '').trim().replace(/\.$/, '');
      const examplesRaw = (tail || '').trim().replace(/\.$/, '');
      const examples = examplesRaw
        ? (examplesRaw.includes(';') ? examplesRaw.split(';') : examplesRaw.split(','))
            .map((e) => e.trim())
            .filter(Boolean)
        : [];

      if (message.startsWith('Duplicate dates detected')) {
        items.push({
          title: 'Duplicate Dates',
          message,
          examples,
        });
        continue;
      }

      if (message.startsWith('Year label mismatch')) {
        items.push({
          title: 'Year Label Mismatch',
          message,
          examples,
        });
        continue;
      }

      others.push(line);
    }

    if (others.length > 0) {
      items.push({
        title: 'Other Issues',
        message: `${others.length} issue(s) found.`,
        examples: others,
      });
    }

    return items;
  }

  // Preview rendering is driven by the backend status (create/update/delete/skip),
  // so we intentionally avoid client-side “value changed” comparisons.

  private maybeLoadOptions(): void {
    if (!this.isAdmin || this.optionsLoaded) {
      return;
    }
    this.optionsLoaded = true;

    Object.keys(this.allowedCategoriesMap).forEach((slug) => {
      this.loadProductChoices(slug);
    });
  }

  private loadProductChoices(slug: string): void {
    this.productService.searchProducts('', 1, 50, { categories: this.allowedCategoriesMap[slug] }).subscribe({
      next: (result) => {
        this.quoteProductChoices[slug] = result.options;
      },
    });
  }

  protected saveQuoteAssignments(): void {
    if (!this.isAdmin || !this.venue?.id) {
      return;
    }

    this.savingAssignments = true;
    const payload: Record<string, { products: number[]; variant_options: number[] }> = {};
    Object.keys(this.quoteAssignmentControls).forEach((key) => {
      payload[key] = {
        products: this.quoteAssignmentControls[key].value || [],
        variant_options: this.quoteVariantOptionControls[key]?.value || [],
      };
    });

    this.venueQuoteAssignmentService.saveAssignments(this.venue.id, payload).subscribe({
      next: (response) => {
        this.savingAssignments = false;
        this.notificationService.success('Quote products saved');
      },
      error: () => {
        this.savingAssignments = false;
        this.notificationService.error('Failed to save quote products');
      },
    });
  }

  /**
   * Handle a drag-and-drop reorder of the product cards within a category. The
   * new card order becomes the order products (and their variant options) appear
   * in that dropdown on the public quote form once saved. This ordering is venue
   * scoped and never touches the event-level product ordering (TVG-81).
   */
  protected onQuoteProductDropped(slug: string, event: CdkDragDrop<ProductModel[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const cards = this.quoteProducts[slug];
    if (!cards) {
      return;
    }

    moveItemInArray(cards, event.previousIndex, event.currentIndex);
    this.syncQuoteOrderFromCards(slug);
  }

  /**
   * Rebuild the assigned product and variant-option id arrays from the current
   * card order so the saved payload (and therefore the public form) reflects the
   * order staff dragged the cards into. Any selected id that has no visible card
   * is preserved at the end so a reorder can never drop a selection.
   */
  private syncQuoteOrderFromCards(slug: string): void {
    const assignmentControl = this.quoteAssignmentControls[slug];
    const variantControl = this.quoteVariantOptionControls[slug];
    const assignedProductIds = new Set<number>(assignmentControl?.value ?? []);
    const selectedOptionIds = new Set<number>(variantControl?.value ?? []);

    const orderedProductIds: number[] = [];
    const orderedOptionIds: number[] = [];

    this.quoteProducts[slug].forEach((product) => {
      if (typeof product.id === 'number' && assignedProductIds.has(product.id)) {
        orderedProductIds.push(product.id);
      }

      this.getSortedVariants(product)
        .flatMap((variant) => this.getSortedOptions(variant))
        .forEach((option) => {
          if (typeof option.id === 'number' && selectedOptionIds.has(option.id)) {
            orderedOptionIds.push(option.id);
          }
        });
    });

    assignedProductIds.forEach((id) => {
      if (!orderedProductIds.includes(id)) {
        orderedProductIds.push(id);
      }
    });
    selectedOptionIds.forEach((id) => {
      if (!orderedOptionIds.includes(id)) {
        orderedOptionIds.push(id);
      }
    });

    if (assignmentControl) {
      assignmentControl.setValue(orderedProductIds);
      assignmentControl.markAsDirty();
    }
    if (variantControl) {
      variantControl.setValue(orderedOptionIds);
      variantControl.markAsDirty();
    }
  }

  private addQuoteProduct(
    slug: string,
    productId: number,
    markDirty: boolean = true,
    forceProductAssignment: boolean = false,
  ): void {
    if (this.quoteProducts[slug].some((product) => product.id === productId)) {
      return;
    }

    this.productService.getById(productId).subscribe({
      next: (response) => {
        if (!response.data?.id) {
          return;
        }

        this.quoteProducts[slug] = [...this.quoteProducts[slug], response.data];
        const shouldAssignProduct = forceProductAssignment || !response.data.is_variant_based;
        if (!shouldAssignProduct) {
          return;
        }

        const control = this.quoteAssignmentControls[slug];
        if (!control) {
          return;
        }
        const current = control.value ? [...control.value] : [];
        if (!current.includes(response.data.id)) {
          control.setValue([...current, response.data.id]);
          if (markDirty) {
            control.markAsDirty();
          }
        }
      },
    });
  }

  protected removeQuoteProduct(slug: string, product: ProductModel): void {
    if (!product.id) {
      return;
    }

    const optionIds = this.flattenVariantOptions(product).map((option) => option.id).filter(Boolean) as number[];
    if (optionIds.length) {
      this.removeVariantOptions(slug, optionIds);
    }

    const control = this.quoteAssignmentControls[slug];
    if (control) {
      const current = control.value ? [...control.value] : [];
      control.setValue(current.filter((id) => id !== product.id));
      control.markAsDirty();
    }

    this.quoteProducts[slug] = this.quoteProducts[slug].filter((item) => item.id !== product.id);
  }

  protected isVariantOptionSelected(slug: string, optionId?: number | null): boolean {
    if (!optionId) {
      return false;
    }

    return (this.quoteVariantOptionControls[slug]?.value || []).includes(optionId);
  }

  protected toggleVariantOption(slug: string, optionId?: number | null): void {
    if (!optionId) {
      return;
    }

    const control = this.quoteVariantOptionControls[slug];
    if (!control) {
      return;
    }

    const current = control.value ? [...control.value] : [];
    if (current.includes(optionId)) {
      control.setValue(current.filter((id) => id !== optionId));
    } else {
      control.setValue([...current, optionId]);
    }
    control.markAsDirty();
  }

  protected getSortedVariants(product: ProductModel): ProductVariantModel[] {
    return [...(product.variants || [])].sort((a, b) => (a.id || 0) - (b.id || 0));
  }

  protected getSortedOptions(variant: ProductVariantModel): ProductVariantOptionModel[] {
    return [...(variant.options || [])]
      .filter((option) => !option.category || option.category === 'Full Price')
      .sort((a, b) => (a.id || 0) - (b.id || 0));
  }

  protected getProductPriceLabel(product: ProductModel): string | null {
    if (product.price_formatted) {
      return product.price_formatted;
    }

    if (typeof product.price === 'number') {
      return this.formatPrice(product.price);
    }

    const prices = this.getSortedVariants(product)
      .flatMap((variant) => this.getSortedOptions(variant))
      .map((option) => option.price)
      .filter((price): price is number => typeof price === 'number');

    if (prices.length === 0) {
      return null;
    }

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    if (minPrice === maxPrice) {
      return this.formatPrice(minPrice);
    }

    return `${this.formatPrice(minPrice)}–${this.formatPrice(maxPrice)}`;
  }

  private flattenVariantOptions(product: ProductModel): ProductVariantOptionModel[] {
    return (product.variants || []).flatMap((variant) => variant.options || []);
  }

  private removeVariantOptions(slug: string, optionIds: number[]): void {
    const control = this.quoteVariantOptionControls[slug];
    if (!control) {
      return;
    }

    const current = control.value ? [...control.value] : [];
    control.setValue(current.filter((id) => !optionIds.includes(id)));
    control.markAsDirty();
  }

  protected formatOptionPrice(option: ProductVariantOptionModel): string {
    if (option.price === null || option.price === undefined) {
      return this.formatPrice(0);
    }

    return this.formatPrice(option.price);
  }

  protected formatOptionMeta(option: ProductVariantOptionModel): string {
    const price = this.formatOptionPrice(option);
    const category = option.category?.trim();

    if (!category || category === 'Full Price') {
      return price;
    }

    return `${category} - ${price}`;
  }

  private formatPrice(price: number): string {
    return `£${price.toFixed(2)}`;
  }
}
